import { Typography } from '@/shared/components/ui/typography'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { cn } from '@/shared/lib/utils'
import { ComponentProps } from 'react'
import { SETTINGS_SECTIONS } from '../constants/settings.constants'
import { SettingsSection } from '../types/settings.types'

type SettingSidebarProps = ComponentProps<'div'> & {
  activeSection: SettingsSection
  onSectionChange: (section: SettingsSection) => void
}

type NavItem = {
  id: SettingsSection
  label: string
  icon: React.ComponentType<{ className?: string }>
  enabled: boolean
}

export const SettingsSidebar = ({
  activeSection,
  onSectionChange,
  ...props
}: SettingSidebarProps) => {
  const { flags: [
    isNotificationsManagementEnabled,
    isWorkspacePeopleManagementEnabled,
    isWorkspaceSettingsEnabled,
    isWorkspacePlansEnabled,
  ] } = useFeatureFlagsWithWorkspace([
    'settingsNotificationsManagement',
    'settingsWorkspacePeopleManagement',
    'settingsWorkspace',
    'settingsWorkspacePlans',
  ])

  const { workspaceId } = useWorkspaceContext()

  const WorkspaceIcon = ({ className }: { className?: string }) => (
    <div
      className={cn(
        'flex h-4 w-4 items-center justify-center rounded bg-purple-500',
        className
      )}
    >
      <span className="text-xs font-bold text-white">M</span>
    </div>
  )

  const workspaceItems: NavItem[] = [
    {
      id: SETTINGS_SECTIONS.workspace.id,
      label: SETTINGS_SECTIONS.workspace.label,
      icon: WorkspaceIcon,
      enabled: isWorkspaceSettingsEnabled,
    },
    {
      id: SETTINGS_SECTIONS.people.id,
      label: SETTINGS_SECTIONS.people.label,
      icon: SETTINGS_SECTIONS.people.icon,
      enabled: isWorkspacePeopleManagementEnabled,
    },
    {
      id: SETTINGS_SECTIONS.plans.id,
      label: SETTINGS_SECTIONS.plans.label,
      icon: SETTINGS_SECTIONS.plans.icon,
      enabled: isWorkspacePlansEnabled,
    },
  ]

  const settingsItems: NavItem[] = [
    {
      id: SETTINGS_SECTIONS.profile.id,
      label: SETTINGS_SECTIONS.profile.label,
      icon: SETTINGS_SECTIONS.profile.icon,
      enabled: true,
    },
    {
      id: SETTINGS_SECTIONS.aiSettings.id,
      label: SETTINGS_SECTIONS.aiSettings.label,
      icon: SETTINGS_SECTIONS.aiSettings.icon,
      enabled: true,
    },
    {
      id: SETTINGS_SECTIONS.notifications.id,
      label: SETTINGS_SECTIONS.notifications.label,
      icon: SETTINGS_SECTIONS.notifications.icon,
      enabled: isNotificationsManagementEnabled,
    },
  ]

  const renderNavItem = (item: NavItem) => (
    <button
      key={item.id}
      onClick={() => onSectionChange(item.id)}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg p-2 pr-0 text-left text-sm font-medium',
        activeSection === item.id
          ? 'bg-grey-03/30 text-gray-900'
          : 'text-gray-700 hover:bg-grey-03/30 hover:text-gray-900'
      )}
    >
      <item.icon className="h-4 w-4" />
      {item.label}
    </button>
  )

  const filteredWorkspaceItems = workspaceItems.filter((item) => item.enabled)
  const filteredSettingsItems = settingsItems.filter((item) => item.enabled)

  return (
    <div
      {...props}
      className={cn(
        'flex flex-col gap-6 p-3 md:p-4 md:pl-6 md:pt-8',
        props.className
      )}
    >
      {!!workspaceId && filteredWorkspaceItems.length > 0 && (
        <div className="flex flex-col gap-4">
          <Typography className="pl-2 text-sm font-semibold" as="h2">
            Workspace
          </Typography>
          <div className="flex flex-col gap-1">
            {filteredWorkspaceItems.map(renderNavItem)}
          </div>
        </div>
      )}

      {filteredSettingsItems.length > 0 && (
        <div className="flex flex-col gap-4">
          <Typography className="pl-2 text-sm font-semibold" as="h2">
            Settings
          </Typography>
          <div className="flex flex-col gap-1">
            {filteredSettingsItems.map(renderNavItem)}
          </div>
        </div>
      )}
    </div>
  )
}
